import { Table } from '@mantine/core';
import { mergeRefs } from '@mantine/hooks';
import clsx from 'clsx';
import { useUnit } from 'effector-react';
import { ComponentPropsWithoutRef, forwardRef, useEffect, useRef } from 'react';
import { Row, RegistryModel } from 'shared/lib';

export type RegistryTableClassName = 'root' | 'table';

export type RegistryTableProps = ComponentPropsWithoutRef<'div'> & {
	classNames?: Partial<Record<RegistryTableClassName, string>>;
};

export const createRegistryTable = <
	TRow extends Row,
	TFilters extends Record<string, unknown>,
	TError,
>(
	model: RegistryModel<TRow, TFilters, TError>,
) => {
	const RegistryTable = forwardRef<HTMLDivElement, RegistryTableProps>(
		({ className, classNames, children, ...props }, ref) => {
			const innerRef = useRef<HTMLDivElement>(null);
			const tableRef = useUnit(model.tableRef);
			const [empty, rowsEmpty] = useUnit([model.$empty, model.$rowsEmpty]);

			useEffect(() => {
				innerRef.current && tableRef.setValue(innerRef.current);

				return () => {
					tableRef.setValue(null);
				};
			}, [empty, rowsEmpty]);

			return (
				!empty &&
				!rowsEmpty && (
					<div
						ref={mergeRefs(innerRef, ref)}
						className={clsx('registry-table-root', className, classNames?.root)}
						{...props}
					>
						<Table
							data-test-id='registry-table'
							className={clsx('registry-table-table', classNames?.table)}
						>
							{children}
						</Table>
					</div>
				)
			);
		},
	);

	return RegistryTable;
};
